// State-tax clients API — typed fetch + React Query hook for the client list.
//
// Source of truth for clients is Dataverse (cr8f7_salestaxclientses); this layer
// just surfaces what the backend reads from there. See api/Functions/StateTaxClientFunctions.cs
// and api/Services/DataverseService.GetActiveClientsAsync.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { requireApiAccessToken } from './authToken';
import type { ApiEnvelope, BulkClientUpdateResult, BulkObligationUpdateResult, ClientBilling, ClientNote, ClientSnooze, ComplianceNotification, FilingActivityItem, FilingExtractionResult, ManualFiling, NameMismatchAck, ObligationEvent, ObligationFrequencyChange, ObligationTarget, OpenTaskReassignmentPreview, StaffUser, StateTaxClientSummary, StateTaxObligationSummary, SuggestedObligation, FilingHealthSummary, ClientOwedStatus } from '../types';
import type { ObligationEndReason } from '../utils/obligationEndReasons';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';
const TIMEOUT_MS = 30_000;

/**
 * Thrown by fetchApi on non-2xx responses. Carries the HTTP status so callers
 * can distinguish recoverable errors (e.g. 409 stale-data) from generic
 * failures without parsing message strings.
 */
export class ApiError extends Error {
  public readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function fetchApi<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const token = await requireApiAccessToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    headers['Authorization'] = `Bearer ${token}`;

    if (options?.headers) {
      Object.assign(headers, options.headers as Record<string, string>);
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
      signal: controller.signal,
    });

    if (!response.ok) {
      const errorBody = (await response.json().catch(() => ({ error: 'Unknown error' }))) as { error?: string };
      throw new ApiError(errorBody.error ?? `API Error: ${response.status}`, response.status);
    }

    return response.json() as Promise<T>;
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Request timed out. Please try again.');
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

export const stateTaxClientKeys = {
  all: ['state-tax-clients'] as const,
  list: () => [...stateTaxClientKeys.all, 'list'] as const,
  detail: (id: string) => [...stateTaxClientKeys.all, 'detail', id] as const,
  obligations: (id: string) => [...stateTaxClientKeys.all, 'obligations', id] as const,
  suggestions: (id: string) => [...stateTaxClientKeys.all, 'suggestions', id] as const,
  notes: (id: string) => [...stateTaxClientKeys.all, 'notes', id] as const,
  notifications: (id: string) => [...stateTaxClientKeys.all, 'notifications', id] as const,
  obligationEvents: (clientId: string, obligationId: string) =>
    [...stateTaxClientKeys.all, 'obligationEvents', clientId, obligationId] as const,
  clientObligationEvents: (clientId: string) =>
    [...stateTaxClientKeys.all, 'clientObligationEvents', clientId] as const,
};

export async function fetchActiveStateTaxClients(): Promise<StateTaxClientSummary[]> {
  const envelope = await fetchApi<ApiEnvelope<StateTaxClientSummary[]>>('/state-tax/clients');
  return envelope.data ?? [];
}

export async function fetchStateTaxClient(id: string): Promise<StateTaxClientSummary> {
  const envelope = await fetchApi<ApiEnvelope<StateTaxClientSummary>>(
    `/state-tax/clients/${encodeURIComponent(id)}`,
  );
  if (!envelope.data) throw new Error(envelope.error ?? 'Client not found.');
  return envelope.data;
}

export async function fetchClientStateTaxObligations(id: string): Promise<StateTaxObligationSummary[]> {
  const envelope = await fetchApi<ApiEnvelope<StateTaxObligationSummary[]>>(
    `/state-tax/clients/${encodeURIComponent(id)}/state-tax-obligations`,
  );
  return envelope.data ?? [];
}

export function useStateTaxClients() {
  return useQuery({
    queryKey: stateTaxClientKeys.list(),
    queryFn: fetchActiveStateTaxClients,
  });
}

export function useStateTaxClient(id: string) {
  return useQuery({
    queryKey: stateTaxClientKeys.detail(id),
    queryFn: () => fetchStateTaxClient(id),
    enabled: !!id,
  });
}

export function useClientStateTaxObligations(id: string) {
  return useQuery({
    queryKey: stateTaxClientKeys.obligations(id),
    queryFn: () => fetchClientStateTaxObligations(id),
    enabled: !!id,
  });
}

export interface CreateStateTaxObligationPayload {
  taxType: string;
  state?: string;
  filingFrequency?: string;
  informationSource?: string;
  hasDirectAccess?: boolean;
  notes?: string;
  active?: boolean;
  effectiveStartDate?: string;
}

export async function createClientStateTaxObligation(
  stateTaxClientId: string,
  payload: CreateStateTaxObligationPayload,
): Promise<StateTaxObligationSummary> {
  const envelope = await fetchApi<ApiEnvelope<StateTaxObligationSummary>>(
    `/state-tax/clients/${encodeURIComponent(stateTaxClientId)}/state-tax-obligations`,
    { method: 'POST', body: JSON.stringify(payload) },
  );
  if (!envelope.data) throw new Error(envelope.error ?? 'Create obligation failed.');
  return envelope.data;
}

export function useCreateClientStateTaxObligation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      stateTaxClientId,
      payload,
    }: {
      stateTaxClientId: string;
      payload: CreateStateTaxObligationPayload;
    }) => createClientStateTaxObligation(stateTaxClientId, payload),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({
        queryKey: stateTaxClientKeys.obligations(variables.stateTaxClientId),
      });
    },
  });
}

export interface UpdateStateTaxObligationPayload {
  taxType?: string;
  state?: string;
  filingFrequency?: string;
  informationSource?: string;
  hasDirectAccess?: boolean;
  notes?: string;
  active?: boolean;
  effectiveStartDate?: string;
  // F2 attribution — populated from useMsal().accounts[0] in the edit dialog so
  // ObligationEvent rows can be attributed to the staff member who made the
  // change. Optional today; auth-middleware slice will harden later.
  changedByName?: string;
  changedById?: string;
  reason?: string;
  // Dataverse @odata.etag captured at read time. When present, the backend
  // sends an If-Match header on the PATCH; a server 409 means another writer
  // beat us to it and the dialog surfaces a "stale data — reload" affordance.
  // Omit to keep last-write-wins behavior.
  expectedEtag?: string;
}

export async function updateClientStateTaxObligation(
  stateTaxClientId: string,
  obligationId: string,
  payload: UpdateStateTaxObligationPayload,
): Promise<StateTaxObligationSummary> {
  const envelope = await fetchApi<ApiEnvelope<StateTaxObligationSummary>>(
    `/state-tax/clients/${encodeURIComponent(stateTaxClientId)}/state-tax-obligations/${encodeURIComponent(obligationId)}`,
    { method: 'PATCH', body: JSON.stringify(payload) },
  );
  if (!envelope.data) throw new Error(envelope.error ?? 'Update obligation failed.');
  return envelope.data;
}

export async function fetchObligationTaxTypesByClient(): Promise<Record<string, string[]>> {
  const envelope = await fetchApi<ApiEnvelope<Record<string, string[]>>>(
    '/state-tax/obligations/by-client',
  );
  return envelope.data ?? {};
}

export function useObligationTaxTypesByClient() {
  return useQuery({
    queryKey: [...stateTaxClientKeys.all, 'tax-types-by-client'] as const,
    queryFn: fetchObligationTaxTypesByClient,
  });
}

export async function fetchFilingHealthByClient(): Promise<Record<string, FilingHealthSummary>> {
  const envelope = await fetchApi<ApiEnvelope<Record<string, FilingHealthSummary>>>(
    '/state-tax/obligations/filing-health',
  );
  return envelope.data ?? {};
}

export function useFilingHealthByClient() {
  return useQuery({
    queryKey: [...stateTaxClientKeys.all, 'filing-health-by-client'] as const,
    queryFn: fetchFilingHealthByClient,
  });
}

// FB-2: each client's obligation lifecycle (open / ended / closed). Empty when Dataverse is unavailable, so the dashboard
// keeps showing every client.
export async function fetchClientOwedStatus(): Promise<Record<string, ClientOwedStatus>> {
  const envelope = await fetchApi<ApiEnvelope<Record<string, ClientOwedStatus>>>('/state-tax/obligations/owed-status');
  return envelope.data ?? {};
}

export function useClientOwedStatus() {
  return useQuery({
    queryKey: [...stateTaxClientKeys.all, 'owed-status'] as const,
    queryFn: fetchClientOwedStatus,
  });
}

export async function fetchSuggestedObligations(
  stateTaxClientId: string,
): Promise<SuggestedObligation[]> {
  const envelope = await fetchApi<ApiEnvelope<SuggestedObligation[]>>(
    `/state-tax/clients/${encodeURIComponent(stateTaxClientId)}/suggested-obligations`,
  );
  return envelope.data ?? [];
}

export function useSuggestedObligations(stateTaxClientId: string) {
  return useQuery({
    queryKey: stateTaxClientKeys.suggestions(stateTaxClientId),
    queryFn: () => fetchSuggestedObligations(stateTaxClientId),
    enabled: !!stateTaxClientId,
  });
}

export function useUpdateClientStateTaxObligation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      stateTaxClientId,
      obligationId,
      payload,
    }: {
      stateTaxClientId: string;
      obligationId: string;
      payload: UpdateStateTaxObligationPayload;
    }) => updateClientStateTaxObligation(stateTaxClientId, obligationId, payload),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({
        queryKey: stateTaxClientKeys.obligations(variables.stateTaxClientId),
      });
      // F2: an obligation edit may have produced ObligationEvents — refresh the
      // history popover for that obligation if it's open AND the per-client
      // cross-obligation feed.
      void qc.invalidateQueries({
        queryKey: stateTaxClientKeys.obligationEvents(variables.stateTaxClientId, variables.obligationId),
      });
      void qc.invalidateQueries({
        queryKey: stateTaxClientKeys.clientObligationEvents(variables.stateTaxClientId),
      });
    },
    onError: (err, variables) => {
      // Stale-data conflict — the row changed under us. Invalidate obligations
      // so reopening the dialog picks up the fresh values and the new etag.
      if (err instanceof ApiError && err.status === 409) {
        void qc.invalidateQueries({
          queryKey: stateTaxClientKeys.obligations(variables.stateTaxClientId),
        });
      }
    },
  });
}

/** An open task that still carries an unfiled filing of a just-removed obligation type. */
export interface RemovedObligationOpenTask {
  id: string;
  periodLabel: string;
  dueDate: string;
  status: string;
}

export interface RemoveObligationResult {
  openTasks: RemovedObligationOpenTask[];
}

export async function removeClientStateTaxObligation(
  stateTaxClientId: string,
  obligationId: string,
): Promise<RemoveObligationResult> {
  // Call fetch directly to avoid fetchApi's JSON-body assumption (deleteManualFiling
  // precedent). Removal soft-deletes in whichever store owns the id; filed history and
  // existing tasks are untouched (spec 2026-07-14). The 200 body lists open tasks that
  // still carry an unfiled filing of the removed type so staff can review/cancel them —
  // a removed obligation does NOT stop reminders on an already-generated task (the
  // Venemex Pro incident). Advisory only: a 204 or unparseable body degrades to empty.
  const token = await requireApiAccessToken();
  const headers: Record<string, string> = {};
  headers['Authorization'] = `Bearer ${token}`;
  const response = await fetch(
    `${API_BASE}/state-tax/clients/${encodeURIComponent(stateTaxClientId)}/state-tax-obligations/${encodeURIComponent(obligationId)}`,
    { method: 'DELETE', headers },
  );
  if (!response.ok && response.status !== 204) {
    const body = (await response.json().catch(() => ({ error: 'Unknown error' }))) as { error?: string };
    throw new Error(body.error ?? `API Error: ${response.status}`);
  }
  if (response.status === 204) return { openTasks: [] };
  const body = (await response.json().catch(() => null)) as
    | { data?: { openTasks?: RemovedObligationOpenTask[] } }
    | null;
  return { openTasks: body?.data?.openTasks ?? [] };
}

export function useRemoveClientStateTaxObligation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ stateTaxClientId, obligationId }: { stateTaxClientId: string; obligationId: string }) =>
      removeClientStateTaxObligation(stateTaxClientId, obligationId),
    onSuccess: (_data, variables) => {
      // Removal is rare — a wide invalidation blast radius is safer than key whack-a-mole.
      // The all-prefix covers obligations, suggestions, detail, next-due, obligation events,
      // obligations-by-client aggregates, filing views, etc.
      void qc.invalidateQueries({ queryKey: stateTaxClientKeys.all });
      // Process-as-task picker preview (FilingPeriodActionDialog).
      void qc.invalidateQueries({ queryKey: ['task-generation-preview', variables.stateTaxClientId] });
      // complianceApi's dashboardKeys.all / obligationKeys.all — as literals because
      // complianceApi imports stateTaxClientKeys from this module (importing the
      // factories back here would create a module cycle).
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
      void qc.invalidateQueries({ queryKey: ['obligations'] });
    },
  });
}

/** A future task cancelled (or flagged for review) by an effective-dated obligation end. */
export interface EndedObligationTask {
  id: string;
  periodLabel: string;
  dueDate: string;
  status: string;
}

export interface EndObligationResult {
  /** Wholly-attributable future tasks the server cancelled. */
  cancelledTasks: EndedObligationTask[];
  /** Multi-obligation future tasks the server did NOT cancel — staff review manually. */
  reviewTasks: EndedObligationTask[];
}

export interface EndObligationPayload {
  /** Effective end date (yyyy-MM-dd). Generation stops any period that STARTS after it. */
  effectiveEndDate: string;
  /** Staff-chosen label — identical behavior either way. */
  status: 'ended' | 'cancelled';
  /**
   * WHY it ended. Required, and fail-closed on the server: anything outside
   * OBLIGATION_END_REASONS is a 400, never coerced. Mirrors api/Models/ObligationEndReason.cs.
   */
  endReason: ObligationEndReason;
  /** Optional free-text note alongside the code. */
  reason?: string;
  changedByName?: string;
  changedById?: string;
}

// POST .../state-tax-obligations/{id}/end — effective-dated END / CANCEL (spec 2026-07-15).
// Preserves the obligation + history; sets its effective end so future periods stop generating,
// and cancels open future tasks wholly attributable to it (multi-obligation tasks are returned
// as reviewTasks for manual handling). Distinct from removeClientStateTaxObligation (no-date
// hard-remove for mistakes). Direct fetch (deleteManualFiling precedent) to keep the typed body.
export async function endClientStateTaxObligation(
  stateTaxClientId: string,
  obligationId: string,
  payload: EndObligationPayload,
): Promise<EndObligationResult> {
  const token = await requireApiAccessToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  headers['Authorization'] = `Bearer ${token}`;
  const response = await fetch(
    `${API_BASE}/state-tax/clients/${encodeURIComponent(stateTaxClientId)}/state-tax-obligations/${encodeURIComponent(obligationId)}/end`,
    { method: 'POST', headers, body: JSON.stringify(payload) },
  );
  if (!response.ok) {
    const body = (await response.json().catch(() => ({ error: 'Unknown error' }))) as { error?: string };
    throw new Error(body.error ?? `API Error: ${response.status}`);
  }
  const body = (await response.json().catch(() => null)) as
    | { data?: { cancelledTasks?: EndedObligationTask[]; reviewTasks?: EndedObligationTask[] } }
    | null;
  return {
    cancelledTasks: body?.data?.cancelledTasks ?? [],
    reviewTasks: body?.data?.reviewTasks ?? [],
  };
}

export function useEndClientStateTaxObligation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ stateTaxClientId, obligationId, payload }: {
      stateTaxClientId: string;
      obligationId: string;
      payload: EndObligationPayload;
    }) => endClientStateTaxObligation(stateTaxClientId, obligationId, payload),
    onSuccess: (_data, variables) => {
      // Same wide blast radius as removal (rare op): obligations, suggestions, detail,
      // next-due, obligation events, filing views, etc.
      void qc.invalidateQueries({ queryKey: stateTaxClientKeys.all });
      void qc.invalidateQueries({ queryKey: ['task-generation-preview', variables.stateTaxClientId] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
      void qc.invalidateQueries({ queryKey: ['obligations'] });
      // Unlike removal, the END flow CANCELS open future tasks — so the Compliance Tasks
      // board must refresh or it keeps showing tasks this end just cancelled. This is
      // complianceApi's taskKeys.all; written as the ['tasks'] literal (same value) because
      // complianceApi imports stateTaxClientKeys from this module — importing taskKeys back
      // here would create a module cycle (same reason dashboard/obligations are literals above).
      void qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

export interface ReactivateObligationPayload {
  reason?: string;
  changedByName?: string;
  changedById?: string;
}

// POST .../state-tax-obligations/{id}/reactivate — reverses an effective-dated end/cancel
// (spec 2026-07-15). Clears the obligation's effective end date so future generation resumes;
// does NOT reopen the tasks the end cancelled (staff reopen those individually). Direct fetch
// (endClientStateTaxObligation precedent) to keep the typed body + auth header.
export async function reactivateClientStateTaxObligation(
  stateTaxClientId: string,
  obligationId: string,
  payload?: ReactivateObligationPayload,
): Promise<void> {
  const token = await requireApiAccessToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  headers['Authorization'] = `Bearer ${token}`;
  const response = await fetch(
    `${API_BASE}/state-tax/clients/${encodeURIComponent(stateTaxClientId)}/state-tax-obligations/${encodeURIComponent(obligationId)}/reactivate`,
    { method: 'POST', headers, body: JSON.stringify(payload ?? {}) },
  );
  if (!response.ok) {
    const body = (await response.json().catch(() => ({ error: 'Unknown error' }))) as { error?: string };
    throw new Error(body.error ?? `API Error: ${response.status}`);
  }
}

export function useReactivateClientStateTaxObligation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ stateTaxClientId, obligationId, payload }: {
      stateTaxClientId: string;
      obligationId: string;
      payload?: ReactivateObligationPayload;
    }) => reactivateClientStateTaxObligation(stateTaxClientId, obligationId, payload),
    onSuccess: (_data, variables) => {
      // Same wide blast radius as the end flow (rare op): obligations, suggestions, detail,
      // next-due, obligation events, filing views, etc.
      void qc.invalidateQueries({ queryKey: stateTaxClientKeys.all });
      void qc.invalidateQueries({ queryKey: ['task-generation-preview', variables.stateTaxClientId] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
      void qc.invalidateQueries({ queryKey: ['obligations'] });
      // Reactivation RESUMES future task generation (the gate keys on EndDate, now cleared), so
      // the Compliance Tasks board must refresh. This is complianceApi's taskKeys.all; written as
      // the ['tasks'] literal (same value) to avoid a module cycle — same as the end flow above.
      void qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

// Picklist options match the Dataverse cr8f7_statetaxobligation entity. Source of truth
// is scripts/inspect-picklists.cjs output (committed labels, runtime values are the int
// codes the backend maps from these labels). Reemployment Tax is intentionally excluded
// from the dropdown — it lives on the payroll side and is handled separately.
export const STATE_TAX_OBLIGATION_OPTIONS = {
  taxType: [
    'Sales Tax',
    'Communication Services Tax',
    'Solid Waste Fees and Surcharge Tax',
    'Documentary Stamp Tax Filing',
    'Motor and Other Fuel Tax',
    'Gross Receipts Tax',
    'Tangible Personal Property Tax',
  ],
  state: ['FL', 'GA', 'TN', 'CA', 'WA', 'TX', 'NY'],
  filingFrequency: ['Monthly', 'Quarterly', 'Semi-Annually', 'Annually'],
  informationSource: [
    'POS Statements',
    'Bank Statements',
    'Submit Invoices',
    'Quickbooks Online',
    'Quickbooks',
    'Rental (Always the same)',
    'Xero (In House)',
  ],
} as const;

// ── Bookkeeping cross-reference ────────────────────────────────────────────────
// Set of accountIds for clients that also have an active cr8f7_bookkeeping row.
// Used to render a "Bookkeeping" chip on the clients list and to filter to overlap
// clients only. Backed by one Dataverse query, ~kilobytes of payload, safe to cache.

export async function fetchBookkeepingAccountIds(): Promise<string[]> {
  const envelope = await fetchApi<ApiEnvelope<string[]>>(
    '/state-tax/clients/bookkeeping-account-ids',
  );
  return envelope.data ?? [];
}

export function useBookkeepingAccountIds() {
  return useQuery({
    queryKey: [...stateTaxClientKeys.all, 'bookkeeping-account-ids'] as const,
    queryFn: fetchBookkeepingAccountIds,
    // Bookkeeping client list changes daily-at-most. 5 min cache is plenty.
    staleTime: 5 * 60 * 1000,
  });
}

// ── Next-due aggregate ──────────────────────────────────────────────────────────
// Per-client soonest non-terminal due date (ISO-8601 string), aggregated server-side so
// the clients list renders + sorts its "Next due" column without pulling the whole task
// collection to the browser. Clients with no active task are omitted from the map.

export async function fetchClientsNextDue(): Promise<Record<string, string>> {
  const envelope = await fetchApi<ApiEnvelope<Record<string, string>>>(
    '/state-tax/clients/next-due',
  );
  return envelope.data ?? {};
}

export function useClientsNextDue() {
  return useQuery({
    queryKey: [...stateTaxClientKeys.all, 'next-due'] as const,
    queryFn: fetchClientsNextDue,
  });
}

// ── Filing activity ─────────────────────────────────────────────────────────────
// Chronological feed of filed returns sourced from the dept=Sales Tax Dataverse tasks
// (which carry every obligation type, distinguished by the populated file slot), classified
// to the real obligation type. The "department" label is a misnomer — see the backend.

export async function fetchClientFilingActivity(
  stateTaxClientId: string,
): Promise<FilingActivityItem[]> {
  const envelope = await fetchApi<ApiEnvelope<FilingActivityItem[]>>(
    `/state-tax/clients/${encodeURIComponent(stateTaxClientId)}/filing-activity`,
  );
  return envelope.data ?? [];
}

export function useClientFilingActivity(stateTaxClientId: string) {
  return useQuery({
    queryKey: [...stateTaxClientKeys.all, 'filing-activity', stateTaxClientId] as const,
    queryFn: () => fetchClientFilingActivity(stateTaxClientId),
    enabled: !!stateTaxClientId,
  });
}

// ── Filing documents (PDF view/download) ──────────────────────────────────────────
// Both download paths stream raw bytes (not JSON-enveloped) with Content-Disposition, so they
// bypass fetchApi and attach the bearer token directly — mirrors complianceApi.downloadFilingAttachment.

function parseFileNameFromContentDisposition(header: string): string | null {
  const starMatch = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (starMatch) {
    try { return decodeURIComponent(starMatch[1]); } catch { /* fall through */ }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain?.[1] ?? null;
}

async function downloadBlob(url: string, fallbackName: string): Promise<{ blob: Blob; fileName: string }> {
  const token = await requireApiAccessToken();
  const headers: Record<string, string> = {};
  headers['Authorization'] = `Bearer ${token}`;
  const response = await fetch(url, { headers });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({ error: 'Download failed' }))) as { error?: string };
    throw new ApiError(body.error ?? `Download failed: ${response.status}`, response.status);
  }
  const fileName = parseFileNameFromContentDisposition(
    response.headers.get('Content-Disposition') ?? '',
  ) ?? fallbackName;
  const blob = await response.blob();
  return { blob, fileName };
}

/**
 * Fetch a Dataverse-sourced filing's return PDF (task File column) as a Blob.
 * Pass inline=true for the View-PDF path so the server sends Content-Disposition: inline
 * and the browser renders it in a tab instead of forcing a download.
 */
export function downloadDataverseFilingDocument(
  stateTaxClientId: string,
  activityId: string,
  slot: string,
  fileName?: string,
  inline = false,
): Promise<{ blob: Blob; fileName: string }> {
  const params = new URLSearchParams();
  if (fileName) params.set('fileName', fileName);
  if (inline) params.set('inline', 'true');
  const qs = params.toString() ? `?${params.toString()}` : '';
  return downloadBlob(
    `${API_BASE}/state-tax/clients/${encodeURIComponent(stateTaxClientId)}` +
      `/filings/dataverse/${encodeURIComponent(activityId)}/${encodeURIComponent(slot)}/document${qs}`,
    fileName ?? 'filing',
  );
}

/**
 * Fetch a manual filing's attached PDF (blob storage) as a Blob. inline=true → render-in-tab.
 * `which` selects the return PDF (default) or the separate payment-confirmation PDF.
 */
export function downloadManualFilingDocument(
  stateTaxClientId: string,
  manualFilingId: string,
  inline = false,
  which: 'return' | 'payment' = 'return',
): Promise<{ blob: Blob; fileName: string }> {
  const params = new URLSearchParams();
  if (inline) params.set('inline', 'true');
  if (which === 'payment') params.set('which', 'payment');
  const qs = params.toString() ? `?${params.toString()}` : '';
  return downloadBlob(
    `${API_BASE}/state-tax/clients/${encodeURIComponent(stateTaxClientId)}` +
      `/manual-filings/${encodeURIComponent(manualFilingId)}/document${qs}`,
    'filing',
  );
}

// ── Manual filings (app-owned override) ────────────────────────────────────────────
// Record a filing that no Dataverse task captured. Surfaced in the recent-history grid +
// filing-activity feed as "filed (manual)". NO Dataverse write. May carry a return PDF.

export async function fetchManualFilings(stateTaxClientId: string): Promise<ManualFiling[]> {
  const envelope = await fetchApi<ApiEnvelope<ManualFiling[]>>(
    `/state-tax/clients/${encodeURIComponent(stateTaxClientId)}/manual-filings`,
  );
  return envelope.data ?? [];
}

export function useManualFilings(stateTaxClientId: string) {
  return useQuery({
    queryKey: [...stateTaxClientKeys.all, 'manual-filings', stateTaxClientId] as const,
    queryFn: () => fetchManualFilings(stateTaxClientId),
    enabled: !!stateTaxClientId,
  });
}

export interface UploadedAttachmentMeta {
  blobName: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
}

/**
 * Extract amounts from a raw PDF for a manual (task-less) filing via GPT.
 * Sends raw bytes to POST /api/state-tax/clients/{id}/manual-filings/extract.
 * Soft-fails: returns Ok=false on extraction failure rather than throwing.
 */
export async function extractManualFiling(
  stateTaxClientId: string,
  taxType: string,
  file: File,
  expect?: 'return' | 'payment',
): Promise<FilingExtractionResult> {
  const params = new URLSearchParams({ taxType, fileName: file.name });
  if (expect) params.set('expect', expect);
  const envelope = await fetchApi<ApiEnvelope<FilingExtractionResult>>(
    `/state-tax/clients/${encodeURIComponent(stateTaxClientId)}/manual-filings/extract?${params.toString()}`,
    {
      method: 'POST',
      body: file,
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
    },
  );
  // Backend never 4xx/5xx on extraction failure — it returns Ok=false in the envelope.
  return envelope.data ?? { ok: false, taxTypeMatch: false, crossCheckPassed: false, confidence: 0 };
}

/** Upload a manual filing's return PDF to blob storage (upload-then-save). Returns the blob metadata. */
export async function uploadManualFilingAttachment(
  stateTaxClientId: string,
  file: File,
): Promise<UploadedAttachmentMeta> {
  const envelope = await fetchApi<ApiEnvelope<UploadedAttachmentMeta>>(
    `/state-tax/clients/${encodeURIComponent(stateTaxClientId)}/manual-filings/attachment` +
      `?fileName=${encodeURIComponent(file.name)}`,
    {
      method: 'POST',
      body: file,
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
    },
  );
  if (!envelope.data) throw new Error(envelope.error ?? 'Attachment upload failed.');
  return envelope.data;
}

export interface RecordManualFilingPayload {
  taxType: string;
  periodStart: string; // ISO date
  cadence?: string;     // FilingCadence enum name; defaults Monthly server-side
  filedOn: string;      // ISO date
  confirmationNumber?: string;
  returnConfirmationNumber?: string;
  paymentConfirmationNumber?: string;
  amount?: number;
  note?: string;
  attachmentBlobName?: string;
  attachmentFileName?: string;
  attachmentContentType?: string;
  paymentAttachmentBlobName?: string;
  paymentAttachmentFileName?: string;
  paymentAttachmentContentType?: string;
  recordedByName?: string;
  bankDebitAmount?: number;
  bankDebitDate?: string;   // ISO date (yyyy-MM-dd)
  bankAccountMasked?: string;
  // Flow C name-mismatch override — set only when the staffer confirmed the
  // NameMismatchWarning confirm-to-save gate; forwarded as-is in the request body.
  nameMismatchAck?: NameMismatchAck;
}

export async function recordManualFiling(
  stateTaxClientId: string,
  payload: RecordManualFilingPayload,
): Promise<ManualFiling> {
  const envelope = await fetchApi<ApiEnvelope<ManualFiling>>(
    `/state-tax/clients/${encodeURIComponent(stateTaxClientId)}/manual-filings`,
    { method: 'POST', body: JSON.stringify(payload) },
  );
  if (!envelope.data) throw new Error(envelope.error ?? 'Failed to record manual filing.');
  return envelope.data;
}

// A manual filing changes both the activity feed and the recent-history grid (obligations) —
// invalidate both so the new "filed (manual)" entry shows immediately.
function invalidateFilingViews(qc: ReturnType<typeof useQueryClient>, stateTaxClientId: string) {
  void qc.invalidateQueries({ queryKey: [...stateTaxClientKeys.all, 'manual-filings', stateTaxClientId] });
  void qc.invalidateQueries({ queryKey: [...stateTaxClientKeys.all, 'filing-activity', stateTaxClientId] });
  void qc.invalidateQueries({ queryKey: stateTaxClientKeys.obligations(stateTaxClientId) });
}

export function useRecordManualFiling() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ stateTaxClientId, payload }: { stateTaxClientId: string; payload: RecordManualFilingPayload }) =>
      recordManualFiling(stateTaxClientId, payload),
    onSuccess: (_data, variables) => invalidateFilingViews(qc, variables.stateTaxClientId),
  });
}

export async function deleteManualFiling(stateTaxClientId: string, filingId: string): Promise<void> {
  // DELETE returns 204 — call fetch directly to avoid fetchApi's JSON-body assumption.
  const token = await requireApiAccessToken();
  const headers: Record<string, string> = {};
  headers['Authorization'] = `Bearer ${token}`;
  const response = await fetch(
    `${API_BASE}/state-tax/clients/${encodeURIComponent(stateTaxClientId)}/manual-filings/${encodeURIComponent(filingId)}`,
    { method: 'DELETE', headers },
  );
  if (!response.ok && response.status !== 204) {
    const body = (await response.json().catch(() => ({ error: 'Unknown error' }))) as { error?: string };
    throw new Error(body.error ?? `API Error: ${response.status}`);
  }
}

export function useDeleteManualFiling() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ stateTaxClientId, filingId }: { stateTaxClientId: string; filingId: string }) =>
      deleteManualFiling(stateTaxClientId, filingId),
    onSuccess: (_data, variables) => invalidateFilingViews(qc, variables.stateTaxClientId),
  });
}

// ── Obligation frequency changes (app-owned, effective-dated) ─────────────────────
// Companion to the cadence-mismatch flag: staff record the actual switch ("Monthly since
// Jan 2026"). Frequency itself stays in Dataverse; this is the app-owned change log.

export async function fetchFrequencyChanges(
  stateTaxClientId: string,
): Promise<ObligationFrequencyChange[]> {
  const envelope = await fetchApi<ApiEnvelope<ObligationFrequencyChange[]>>(
    `/state-tax/clients/${encodeURIComponent(stateTaxClientId)}/frequency-changes`,
  );
  return envelope.data ?? [];
}

export function useObligationFrequencyChanges(stateTaxClientId: string) {
  return useQuery({
    queryKey: [...stateTaxClientKeys.all, 'frequency-changes', stateTaxClientId] as const,
    queryFn: () => fetchFrequencyChanges(stateTaxClientId),
    enabled: !!stateTaxClientId,
  });
}

export interface RecordFrequencyChangePayload {
  taxType: string;
  frequency: string;
  effectiveDate: string; // ISO date
  // Frequency in effect before the switch (usually the obligation's current configured
  // label at record time) — keeps the history grid's pre-change segment correct.
  previousFrequency?: string;
  note?: string;
  recordedByName?: string;
}

export async function recordFrequencyChange(
  stateTaxClientId: string,
  payload: RecordFrequencyChangePayload,
): Promise<ObligationFrequencyChange> {
  const envelope = await fetchApi<ApiEnvelope<ObligationFrequencyChange>>(
    `/state-tax/clients/${encodeURIComponent(stateTaxClientId)}/frequency-changes`,
    { method: 'POST', body: JSON.stringify(payload) },
  );
  if (!envelope.data) throw new Error(envelope.error ?? 'Failed to record frequency change.');
  return envelope.data;
}

export function useRecordFrequencyChange() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ stateTaxClientId, payload }: { stateTaxClientId: string; payload: RecordFrequencyChangePayload }) =>
      recordFrequencyChange(stateTaxClientId, payload),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({
        queryKey: [...stateTaxClientKeys.all, 'frequency-changes', variables.stateTaxClientId],
      });
      void qc.invalidateQueries({ queryKey: stateTaxClientKeys.obligations(variables.stateTaxClientId) });
    },
  });
}

/**
 * Latest recorded frequency change per tax type (most recent effectiveDate wins).
 * Pure — used to surface "{frequency} since {date}" + decide whether a recorded change
 * acknowledges the cadence-mismatch flag.
 */
export function latestFrequencyChangeByType(
  changes: ObligationFrequencyChange[] | undefined,
): Record<string, ObligationFrequencyChange> {
  const out: Record<string, ObligationFrequencyChange> = {};
  for (const c of changes ?? []) {
    const prev = out[c.taxType];
    if (!prev || Date.parse(c.effectiveDate) > Date.parse(prev.effectiveDate)) out[c.taxType] = c;
  }
  return out;
}

// ── Client notes (Cosmos clientNotes container) ────────────────────────────────
// Distinct from the stable `notes` field on StateTaxClientSummary (which mirrors
// Dataverse cr8f7_notes). These are a chronological staff timeline.

export async function fetchClientNotes(stateTaxClientId: string): Promise<ClientNote[]> {
  const envelope = await fetchApi<ApiEnvelope<ClientNote[]>>(
    `/state-tax/clients/${encodeURIComponent(stateTaxClientId)}/notes`,
  );
  return envelope.data ?? [];
}

export function useClientNotes(stateTaxClientId: string) {
  return useQuery({
    queryKey: stateTaxClientKeys.notes(stateTaxClientId),
    queryFn: () => fetchClientNotes(stateTaxClientId),
    enabled: !!stateTaxClientId,
  });
}

export async function fetchClientNotifications(stateTaxClientId: string): Promise<ComplianceNotification[]> {
  const envelope = await fetchApi<ApiEnvelope<ComplianceNotification[]>>(
    `/state-tax/clients/${encodeURIComponent(stateTaxClientId)}/notifications`,
  );
  return envelope.data ?? [];
}

export function useClientNotifications(stateTaxClientId: string) {
  return useQuery({
    queryKey: stateTaxClientKeys.notifications(stateTaxClientId),
    queryFn: () => fetchClientNotifications(stateTaxClientId),
    enabled: !!stateTaxClientId,
  });
}

export async function fetchObligationEvents(
  stateTaxClientId: string,
  obligationId: string,
): Promise<ObligationEvent[]> {
  const envelope = await fetchApi<ApiEnvelope<ObligationEvent[]>>(
    `/state-tax/clients/${encodeURIComponent(stateTaxClientId)}/obligations/${encodeURIComponent(obligationId)}/events`,
  );
  return envelope.data ?? [];
}

export function useObligationEvents(stateTaxClientId: string, obligationId: string) {
  return useQuery({
    queryKey: stateTaxClientKeys.obligationEvents(stateTaxClientId, obligationId),
    queryFn: () => fetchObligationEvents(stateTaxClientId, obligationId),
    enabled: !!stateTaxClientId && !!obligationId,
  });
}

export async function fetchClientObligationEvents(
  stateTaxClientId: string,
): Promise<ObligationEvent[]> {
  const envelope = await fetchApi<ApiEnvelope<ObligationEvent[]>>(
    `/state-tax/clients/${encodeURIComponent(stateTaxClientId)}/obligation-events`,
  );
  return envelope.data ?? [];
}

export function useClientObligationEvents(stateTaxClientId: string) {
  return useQuery({
    queryKey: stateTaxClientKeys.clientObligationEvents(stateTaxClientId),
    queryFn: () => fetchClientObligationEvents(stateTaxClientId),
    enabled: !!stateTaxClientId,
  });
}

export interface CreateClientNotePayload {
  text: string;
  kind?: string;
  createdByName?: string;
  createdById?: string;
}

export async function createClientNote(
  stateTaxClientId: string,
  payload: CreateClientNotePayload,
): Promise<ClientNote> {
  const envelope = await fetchApi<ApiEnvelope<ClientNote>>(
    `/state-tax/clients/${encodeURIComponent(stateTaxClientId)}/notes`,
    { method: 'POST', body: JSON.stringify(payload) },
  );
  if (!envelope.data) throw new Error(envelope.error ?? 'Create note failed.');
  return envelope.data;
}

export function useCreateClientNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      stateTaxClientId,
      payload,
    }: {
      stateTaxClientId: string;
      payload: CreateClientNotePayload;
    }) => createClientNote(stateTaxClientId, payload),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({
        queryKey: stateTaxClientKeys.notes(variables.stateTaxClientId),
      });
    },
  });
}

export async function deleteClientNote(
  stateTaxClientId: string,
  noteId: string,
): Promise<void> {
  // DELETE returns 204 — call fetch directly to avoid fetchApi's JSON-body assumption.
  const token = await requireApiAccessToken();
  const headers: Record<string, string> = {};
  headers['Authorization'] = `Bearer ${token}`;
  const response = await fetch(
    `${API_BASE}/state-tax/clients/${encodeURIComponent(stateTaxClientId)}/notes/${encodeURIComponent(noteId)}`,
    { method: 'DELETE', headers },
  );
  if (!response.ok && response.status !== 204) {
    const errorBody = (await response.json().catch(() => ({ error: 'Unknown error' }))) as { error?: string };
    throw new Error(errorBody.error ?? `API Error: ${response.status}`);
  }
}

export function useDeleteClientNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      stateTaxClientId,
      noteId,
    }: {
      stateTaxClientId: string;
      noteId: string;
    }) => deleteClientNote(stateTaxClientId, noteId),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({
        queryKey: stateTaxClientKeys.notes(variables.stateTaxClientId),
      });
    },
  });
}

export interface UpdateClientNotePayload {
  text?: string;
  kind?: string;
}

export async function updateClientNote(
  stateTaxClientId: string,
  noteId: string,
  payload: UpdateClientNotePayload,
): Promise<ClientNote> {
  const envelope = await fetchApi<ApiEnvelope<ClientNote>>(
    `/state-tax/clients/${encodeURIComponent(stateTaxClientId)}/notes/${encodeURIComponent(noteId)}`,
    { method: 'PATCH', body: JSON.stringify(payload) },
  );
  if (!envelope.data) throw new Error(envelope.error ?? 'Update note failed.');
  return envelope.data;
}

export function useUpdateClientNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      stateTaxClientId,
      noteId,
      payload,
    }: {
      stateTaxClientId: string;
      noteId: string;
      payload: UpdateClientNotePayload;
    }) => updateClientNote(stateTaxClientId, noteId, payload),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({
        queryKey: stateTaxClientKeys.notes(variables.stateTaxClientId),
      });
    },
  });
}

export const CLIENT_NOTE_KINDS = [
  'general',
  'follow-up',
  'phone-call',
  'email',
  'in-person',
] as const;

// ── Management: staff roster + bulk "edit multiple" ────────────────────────────
// Staff roster (active sales-tax admins) feeds the Management page and the owner
// dropdown in the bulk editor. Bulk update applies owner / frequency / info-source
// changes across many clients; the preview surfaces which open tasks would change
// owner so staff can opt into re-stamping them. See StateTaxClientFunctions.cs.

export async function fetchSalesTaxAdmins(): Promise<StaffUser[]> {
  const envelope = await fetchApi<ApiEnvelope<StaffUser[]>>('/state-tax/staff');
  return envelope.data ?? [];
}

export function useSalesTaxAdmins() {
  return useQuery({
    queryKey: [...stateTaxClientKeys.all, 'staff'] as const,
    queryFn: fetchSalesTaxAdmins,
    // Roster changes rarely (config-driven) — cache generously.
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * Whether the signed-in staff member is an ADMIN, answered by the server's own admin gate
 * (`StaffAdminGate`) — the check the admin-only endpoints enforce. Admin-only controls read
 * this, never "is my email on the staff roster": the roster lists preparers and can be derived
 * from client ownership (PR #36), at which point the two stop agreeing.
 * Fail-closed: loading, an error, or a missing field all read as not-admin.
 */
async function fetchAdminStatus(): Promise<boolean> {
  const envelope = await fetchApi<ApiEnvelope<{ isAdmin: boolean }>>('/state-tax/users/me/admin-status');
  return envelope.data?.isAdmin === true;
}

export function useIsStateTaxAdmin() {
  return useQuery({
    queryKey: [...stateTaxClientKeys.all, 'admin-status'] as const,
    queryFn: fetchAdminStatus,
    // Config-driven, like the roster — cache generously.
    staleTime: 10 * 60 * 1000,
  });
}

export async function previewBulkClientUpdate(
  clientIds: string[],
): Promise<OpenTaskReassignmentPreview[]> {
  const envelope = await fetchApi<ApiEnvelope<OpenTaskReassignmentPreview[]>>(
    '/state-tax/clients/bulk-update/preview',
    { method: 'POST', body: JSON.stringify({ clientIds }) },
  );
  return envelope.data ?? [];
}

export function usePreviewBulkClientUpdate() {
  return useMutation({
    mutationFn: (clientIds: string[]) => previewBulkClientUpdate(clientIds),
  });
}

export interface BulkUpdateClientsPayload {
  clientIds: string[];
  ownerSystemUserId?: string;
  ownerName?: string;
  frequency?: string;
  effectiveDate?: string; // ISO date — required by the backend when frequency is set
  informationSource?: string;
  reassignOpenTasks?: boolean;
  changedByName?: string;
  changedById?: string;
  reason?: string;
}

export async function bulkUpdateClients(
  payload: BulkUpdateClientsPayload,
): Promise<BulkClientUpdateResult> {
  const envelope = await fetchApi<ApiEnvelope<BulkClientUpdateResult>>(
    '/state-tax/clients/bulk-update',
    { method: 'POST', body: JSON.stringify(payload) },
  );
  if (!envelope.data) throw new Error(envelope.error ?? 'Bulk update failed.');
  return envelope.data;
}

export function useBulkUpdateClients() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: BulkUpdateClientsPayload) => bulkUpdateClients(payload),
    onSuccess: () => {
      // Owner / frequency / info-source changed on the client records — refresh the list.
      void qc.invalidateQueries({ queryKey: stateTaxClientKeys.list() });
      // Re-stamped open tasks affect next-due assignment views.
      void qc.invalidateQueries({ queryKey: [...stateTaxClientKeys.all, 'next-due'] });
    },
  });
}

// ── Bulk "edit multiple obligations" across clients ────────────────────────────
// Loads the selected clients' obligations (batched), then applies frequency / info-source /
// active / has-direct-access changes across an explicit set of (client, obligation) targets.
// Partial failures ride in the result body. See StateTaxClientFunctions.BulkUpdateObligations.

/** Fetch obligations for several clients at once, keyed by clientId. Falls back to per-client reads. */
export async function fetchObligationsByClients(
  clientIds: string[],
): Promise<Record<string, StateTaxObligationSummary[]>> {
  const unique = Array.from(new Set(clientIds.filter(Boolean)));
  const entries = await Promise.all(
    unique.map(async (id) => [id, await fetchClientStateTaxObligations(id)] as const),
  );
  const result: Record<string, StateTaxObligationSummary[]> = {};
  for (const [id, obs] of entries) result[id] = obs;
  return result;
}

export function useObligationsByClients(clientIds: string[], enabled = true) {
  const key = [...clientIds].sort().join(',');
  return useQuery({
    queryKey: [...stateTaxClientKeys.all, 'obligations-by-clients', key] as const,
    queryFn: () => fetchObligationsByClients(clientIds),
    enabled: enabled && clientIds.length > 0,
  });
}

export interface BulkUpdateObligationsPayload {
  targets: ObligationTarget[];
  frequency?: string;
  effectiveDate?: string; // ISO date — required by the backend when frequency is set
  informationSource?: string;
  active?: boolean;
  hasDirectAccess?: boolean;
  changedByName?: string;
  changedById?: string;
  reason?: string;
}

export async function bulkUpdateObligations(
  payload: BulkUpdateObligationsPayload,
): Promise<BulkObligationUpdateResult> {
  const envelope = await fetchApi<ApiEnvelope<BulkObligationUpdateResult>>(
    '/state-tax/clients/bulk-update-obligations',
    { method: 'POST', body: JSON.stringify(payload) },
  );
  if (!envelope.data) throw new Error(envelope.error ?? 'Bulk obligation update failed.');
  return envelope.data;
}

export function useBulkUpdateObligations() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: BulkUpdateObligationsPayload) => bulkUpdateObligations(payload),
    onSuccess: () => {
      // Obligation fields changed — refresh the list + any obligation reads.
      void qc.invalidateQueries({ queryKey: stateTaxClientKeys.list() });
      void qc.invalidateQueries({ queryKey: [...stateTaxClientKeys.all, 'obligations-by-clients'] });
    },
  });
}

// ── Xero billing (recurring status + overdue + reconciliation) ─────────────────
// Daily-synced Cosmos cache; reads are cheap. The detail card can trigger a manual
// single-client refresh. See api/Functions/BillingFunctions.cs.

export async function fetchBillingSummaries(): Promise<ClientBilling[]> {
  const envelope = await fetchApi<ApiEnvelope<ClientBilling[]>>('/state-tax/billing/summaries');
  return envelope.data ?? [];
}

export function useClientBillingSummaries() {
  return useQuery({
    queryKey: [...stateTaxClientKeys.all, 'billing-summaries'] as const,
    queryFn: fetchBillingSummaries,
    staleTime: 5 * 60 * 1000,
  });
}

export async function fetchClientBilling(stateTaxClientId: string): Promise<ClientBilling | null> {
  try {
    const envelope = await fetchApi<ApiEnvelope<ClientBilling>>(
      `/state-tax/clients/${encodeURIComponent(stateTaxClientId)}/billing`,
    );
    return envelope.data ?? null;
  } catch (err) {
    // 404 = no snapshot synced yet — surface as "no data", not an error.
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

export function useClientBilling(stateTaxClientId: string) {
  return useQuery({
    queryKey: [...stateTaxClientKeys.all, 'billing', stateTaxClientId] as const,
    queryFn: () => fetchClientBilling(stateTaxClientId),
    enabled: !!stateTaxClientId,
  });
}

export async function refreshClientBilling(stateTaxClientId: string): Promise<ClientBilling> {
  const envelope = await fetchApi<ApiEnvelope<ClientBilling>>(
    `/state-tax/clients/${encodeURIComponent(stateTaxClientId)}/billing/refresh`,
    { method: 'POST' },
  );
  if (!envelope.data) throw new Error(envelope.error ?? 'Billing refresh failed.');
  return envelope.data;
}

export function useRefreshClientBilling() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (stateTaxClientId: string) => refreshClientBilling(stateTaxClientId),
    onSuccess: (_data, stateTaxClientId) => {
      void qc.invalidateQueries({ queryKey: [...stateTaxClientKeys.all, 'billing', stateTaxClientId] });
      void qc.invalidateQueries({ queryKey: [...stateTaxClientKeys.all, 'billing-summaries'] });
    },
  });
}

// ── At-risk snoozes (Cosmos-backed, replaces localStorage) ─────────────────────

export const snoozeKeys = {
  all: ['client-snoozes'] as const,
  active: () => [...snoozeKeys.all, 'active'] as const,
};

export async function fetchActiveSnoozes(): Promise<ClientSnooze[]> {
  const envelope = await fetchApi<ApiEnvelope<ClientSnooze[]>>('/state-tax/at-risk-snoozes');
  return envelope.data ?? [];
}

export function useActiveSnoozes() {
  return useQuery({
    queryKey: snoozeKeys.active(),
    queryFn: fetchActiveSnoozes,
    // Snoozes change infrequently — cache 2 minutes between fetches.
    staleTime: 2 * 60 * 1000,
  });
}

export interface SnoozeClientPayload {
  days?: number;
  createdByName?: string;
  createdById?: string;
  reason?: string;
}

export async function snoozeClient(
  stateTaxClientId: string,
  payload: SnoozeClientPayload = {},
): Promise<ClientSnooze> {
  const envelope = await fetchApi<ApiEnvelope<ClientSnooze>>(
    `/state-tax/clients/${encodeURIComponent(stateTaxClientId)}/snooze`,
    { method: 'POST', body: JSON.stringify(payload) },
  );
  if (!envelope.data) throw new Error(envelope.error ?? 'Snooze failed.');
  return envelope.data;
}

export async function unsnoozeClient(stateTaxClientId: string): Promise<void> {
  // DELETE returns 204 No Content — fetchApi expects JSON, so call fetch directly.
  const token = await requireApiAccessToken();
  const headers: Record<string, string> = {};
  headers['Authorization'] = `Bearer ${token}`;
  const response = await fetch(
    `${API_BASE}/state-tax/clients/${encodeURIComponent(stateTaxClientId)}/snooze`,
    { method: 'DELETE', headers },
  );
  if (!response.ok && response.status !== 204) {
    const errorBody = (await response.json().catch(() => ({ error: 'Unknown error' }))) as { error?: string };
    throw new Error(errorBody.error ?? `API Error: ${response.status}`);
  }
}

export function useSnoozeClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      stateTaxClientId,
      payload,
    }: {
      stateTaxClientId: string;
      payload?: SnoozeClientPayload;
    }) => snoozeClient(stateTaxClientId, payload ?? {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: snoozeKeys.active() });
    },
  });
}

export function useUnsnoozeClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (stateTaxClientId: string) => unsnoozeClient(stateTaxClientId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: snoozeKeys.active() });
    },
  });
}

// --- S3 batch2 --- App-owned multi-select information sources ─────────────────

export interface ObligationInfoSource {
  id: string;
  clientId: string;
  obligationId: string;
  sources: string[];
  updatedAt: string;
  updatedBy?: string;
}

export async function fetchObligationInfoSources(clientId: string): Promise<ObligationInfoSource[]> {
  const envelope = await fetchApi<ApiEnvelope<ObligationInfoSource[]>>(
    `/state-tax/clients/${encodeURIComponent(clientId)}/obligation-info-sources`,
  );
  return envelope.data ?? [];
}

export async function upsertObligationInfoSource(
  clientId: string,
  obligationId: string,
  sources: string[],
  updatedBy?: string,
): Promise<ObligationInfoSource> {
  const envelope = await fetchApi<ApiEnvelope<ObligationInfoSource>>(
    `/state-tax/clients/${encodeURIComponent(clientId)}/obligations/${encodeURIComponent(obligationId)}/info-sources`,
    {
      method: 'PUT',
      body: JSON.stringify({ sources, updatedBy }),
    },
  );
  if (!envelope.data) throw new Error(envelope.error ?? 'Upsert failed.');
  return envelope.data;
}

export function useUpsertObligationInfoSource(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      obligationId,
      sources,
      updatedBy,
    }: {
      obligationId: string;
      sources: string[];
      updatedBy?: string;
    }) => upsertObligationInfoSource(clientId, obligationId, sources, updatedBy),
    onSuccess: () => {
      // Invalidate obligations list so the overlay re-fetches on next load.
      void qc.invalidateQueries({ queryKey: stateTaxClientKeys.obligations(clientId) });
    },
  });
}

// --- end S3 batch2 ---
